import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import MaskedInput from "react-text-mask";
import { useState } from "react";
import GoogleMaps from "google-map-react";
import Marker from "../../../../components/Marker";
import { ButtonsWrapper, NextButton, ResetButton } from "../../twStyles";
import { formSelectStyles } from "../DescriptionForm/twStyles";
import { ChevronRight } from "lucide-react";
import api from "../../../../services/api";
import getValidationErrors from "../../../../utils/getValidationError";

import { geocodeByPlaceId } from "react-google-places-autocomplete";
import GeoCode from "react-geocode";

import * as Yup from "yup";
import { useAuth } from "../../../../hooks/auth";
import refreshToken from "../../../../utils/refreshToken";
import { useToast } from "../../../../hooks/toast";
import PropertyChildrenSection from "../../../../components/PropertyChildrenSection";
import type { PropertyChildInput } from "../../../../types/propertyChildren";

interface IProps {
  addressID?: number;
  propertyID?: number;
  localChildren: PropertyChildInput[];
  onLocalChildrenChange: (next: PropertyChildInput[]) => void;
  onChangeCurrentForm: () => void;
  onAddressIdReceived: (id: number) => void;
}

function MapForm({
  addressID,
  propertyID,
  localChildren,
  onLocalChildrenChange,
  onChangeCurrentForm,
  onAddressIdReceived,
}: IProps) {
  const { access_token, dateToExpires } = useAuth();
  const { addToast } = useToast();

  const [address, setAddress] = useState({
    cep: "",
    city: "",
    complement: "",
    floor: "",
    google_id: "",
    latitude: "",
    longitude: "",
    address: "",
    neighborhood: "",
    number: "",
    uf: "",
  });
  const [isSubmiting, setIsSubmiting] = useState(false);
  const [managedChildrenCount, setManagedChildrenCount] = useState(0);

  const [googleMapsSearch, setGoogleMapsSearch] = useState("");

  const [availableAddressInputs, setAvailableAddreInputs] = useState(false);

  function handleChange(identifier: string, value: string) {
    setAddress((prevValue) => ({ ...prevValue, [identifier]: value }));
  }

  async function hadleGetMoreInformationsAboutLocal(
    address: string,
    place_id: string
  ) {
    try {
      GeoCode.setApiKey(
        !!"AIzaSyDSD2QM4JfZsREcRj_8StrW3E587RayIMc"
          ? "AIzaSyDSD2QM4JfZsREcRj_8StrW3E587RayIMc"
          : ""
      );

      const response = await GeoCode.fromAddress(address);

      const request = await geocodeByPlaceId(place_id);

      request[0].address_components.forEach((address) => {
        if (address.types.join().includes("route")) {
          handleChange("address", address.long_name);

          return;
        }

        if (address.types.join().includes("administrative_area_level_2")) {
          handleChange("city", address.long_name);

          return;
        }

        if (address.types.join().includes("sublocality_level_1")) {
          handleChange("neighborhood", address.long_name);

          return;
        }

        if (address.types.join().includes("administrative_area_level_1")) {
          handleChange("uf", address.short_name);

          return;
        }

        if (address.types.join().includes("street_number")) {
          handleChange("number", address.short_name);

          return;
        }

        if (address.types.join().includes("postal_code")) {
          handleChange("cep", address.short_name);

          return;
        }
      });

      handleChange("latitude", response.results[0].geometry.location.lat);
      handleChange("longitude", response.results[0].geometry.location.lng);
    } catch (err:any) {
      console.log(err);
    }
  }

  async function handleAddressSubmit() {
    setIsSubmiting(true);

    try {
      const cepRegex = /^\d{5}-\d{3}$/;

      const schema = Yup.object().shape({
        address: Yup.string()
          .trim()
          .required("O campo Logradouro é obrigatório"),
        cep: Yup.string()
          .trim()
          .required("O campo CEP é obrigatório")
          .matches(cepRegex, "CEP inválido"),
        number: Yup.string()
          .trim()
          .required("O campo Número é obrigatório")
          .matches(/^\d+$/, "O campo Número deve conter apenas dígitos"),
        complement: Yup.string(),
        neighborhood: Yup.string()
          .trim()
          .required("O campo Bairro é obrigatório"),
        floor: Yup.string(),
        city: Yup.string().trim().required("O campo Cidade é obrigatório"),
        uf: Yup.string().trim().required("O campo UF é obrigatório"),
      });

      await schema.validate(address, {
        abortEarly: false,
      });

      const token = await refreshToken(dateToExpires, access_token);

      api.defaults.headers.authorization = `Bearer ${
        !!token ? token : access_token
      }`;

      const addressPayload = {
        ...address,
        number: Number(address.number),
      };

      const response = addressID
        ? await api.put(`/api/adm/addresses/${addressID}`, addressPayload)
        : await api.post("/api/adm/addresses", addressPayload);

      if (!!response.data.success) {
        const savedAddressId = response.data.data?.id ?? addressID;
        if (savedAddressId != null) {
          onAddressIdReceived(savedAddressId);
        }

        addToast({
          title: addressID ? "Endereço atualizado!" : "Endereço cadastrado!",
          type: "success",
        });
        onChangeCurrentForm();
      }
    } catch (err: any) {
      if (err instanceof Yup.ValidationError) {
        const error = getValidationErrors(err);
        const keys = Object.keys(error);
        if (keys.length > 0) {
          addToast({
            title: error[keys[0]],
            type: "info",
            description: "",
          });
        }
      } else {
        const apiErrors = err?.response?.data?.errors;
        if (
          apiErrors != null &&
          typeof apiErrors === "object" &&
          !Array.isArray(apiErrors)
        ) {
          const errorKeys = Object.keys(apiErrors);
          for (let i = 0; i < errorKeys.length; i++) {
            const messages = apiErrors[errorKeys[i]];
            if (Array.isArray(messages)) {
              messages.forEach((msg: string) => {
                addToast({ title: msg, type: "error" });
              });
            }
          }
        }
      }

      if (err?.response?.data?.message) {
        addToast({
          title: err.response.data.message,
          type: "error",
        });
      }

      console.log(err);
    }

    setIsSubmiting(false);
  }

  const fieldClass =
    "flex w-full max-w-[920px] flex-col items-start justify-start gap-2 md:mx-[6px] md:flex-1 [&>label]:mb-1 [&>label]:flex [&>label]:items-end [&>label]:text-sm [&>label]:font-medium [&>label]:text-slate-800 [&>input]:w-full [&>input]:appearance-none [&>input]:rounded-md [&>input]:border [&>input]:border-solid [&>input]:border-slate-200 [&>input]:bg-white [&>input]:px-3 [&>input]:py-2 [&>input]:text-sm [&>input]:text-slate-800 [&>input]:shadow-none [&>input]:outline-none [&>input]:transition [&>input:focus]:border-slate-400 [&>input:focus]:shadow-none [&>input:focus]:ring-0 [&_input]:w-full [&_input]:appearance-none [&_input]:rounded-md [&_input]:border [&_input]:border-solid [&_input]:border-slate-200 [&_input]:bg-white [&_input]:px-3 [&_input]:py-2 [&_input]:text-sm [&_input]:text-slate-800 [&_input]:shadow-none [&_input]:outline-none [&_input]:transition [&_input:focus]:border-slate-400 [&_input:focus]:shadow-none [&_input:focus]:ring-0 [&_input:disabled]:cursor-not-allowed [&_input:disabled]:bg-slate-100 [&_input:disabled]:text-slate-500";

  const useManageChildren =
    Boolean(propertyID) && localChildren.length === 0;
  const hasChildrenUnits = useManageChildren
    ? managedChildrenCount > 0
    : localChildren.length > 0;

  function handleChildrenCountChange(count: number) {
    setManagedChildrenCount(count);
    if (count > 0) {
      setAddress((prev) => ({
        ...prev,
        complement: "",
        floor: "",
      }));
    }
  }

  function handleLocalChildrenChange(next: PropertyChildInput[]) {
    onLocalChildrenChange(next);
    if (next.length > 0) {
      setAddress((prev) => ({
        ...prev,
        complement: "",
        floor: "",
      }));
    }
  }

  return (
    <form
      className="w-full rounded-md border border-slate-200 bg-white p-5 sm:p-8 [&_.css-2b097c-container]:w-full [&_[class*='-control']]:min-h-[44px] [&_[class*='-control']]:rounded-md [&_[class*='-control']]:border [&_[class*='-control']]:border-solid [&_[class*='-control']]:border-slate-200 [&_[class*='-control']]:bg-white [&_[class*='-control']]:shadow-none [&>p]:mb-2 [&>p]:mt-2 [&>p]:text-sm [&>p]:font-medium [&>p]:text-slate-800"
    >
      <p>Endereço completo para busca</p>
      <GooglePlacesAutocomplete
        onLoadFailed={(err) => console.log(err)}
        autocompletionRequest={{
          componentRestrictions: { country: "br" },
          types: ["address"],
          location: {
            lat: -17.221666,
            lng: -46.875,
          },
          bounds: [
            {
              lat: -17.221666,
              lng: -46.875,
            },
            {
              lat: -17.221666,
              lng: -46.875,
            },
          ],
        }}
        selectProps={{
          styles: formSelectStyles,
          inputValue: googleMapsSearch,
          loadingMessage(obj: { inputValue: string }) {
            return "Carregando...";
          },
          onInputChange(e) {
            setGoogleMapsSearch(e);
          },
          onChange(e) {
            hadleGetMoreInformationsAboutLocal(e?.label, e?.value.place_id);

            setAvailableAddreInputs(true);

            setGoogleMapsSearch(e?.label);
            handleChange("google_id", e?.value.place_id ?? address.google_id);
          },
          noOptionsMessage(obj: { inputValue: string }) {
            return "Sem local";
          },
          placeholder: "",
        }}
      />

      <p className="mb-12 mt-3 text-xs font-normal text-[#666666] max-md:text-[10px]">
        Ex: Av. Dom Luís, 500 - Aldeota, Fortaleza - CE, Brasil
      </p>

      <div className="relative mt-8 w-full rounded-md border border-slate-200 bg-slate-50 px-4 py-10 sm:px-6 md:px-10">
        <label className="absolute -top-3 left-1/2 w-[90%] -translate-x-1/2 rounded-md border border-slate-200 bg-white px-4 py-2 text-center text-xs font-medium text-slate-800 md:w-[55%] md:text-sm">
          Confira abaixo se os dados estão corretos:
        </label>

        <div className="mx-auto mt-2 flex max-w-full flex-col items-center justify-center gap-4 md:flex-row md:px-1">
          <div className={fieldClass}>
            <label>Logradouro *:</label>
            <input
              type="text"
              value={address.address}
              readOnly={!availableAddressInputs ? true : false}
              onChange={(e) => handleChange("address", e.target.value)}
            />
          </div>

          <div className={fieldClass}>
            <label>CEP *</label>
            <MaskedInput
              mask={[/\d/, /\d/, /\d/, /\d/, /\d/, "-", /\d/, /\d/, /\d/]}
              type="text"
              value={address.cep}
              readOnly={!availableAddressInputs ? true : false}
              onChange={(e) => handleChange("cep", e.target.value)}
            />
          </div>

          <div className={fieldClass}>
            <label>Número *</label>
            <input
              type="text"
              value={address.number}
              readOnly={!availableAddressInputs ? true : false}
              onChange={(e) => handleChange("number", e.target.value)}
            />
          </div>

          <div className={fieldClass}>
            <label>
              Complemento (opcional)
              {hasChildrenUnits ? (
                <span className="ml-1 text-[10px] font-normal text-slate-500">
                  — definido nas unidades filhas
                </span>
              ) : null}
            </label>
            <input
              type="text"
              value={address.complement}
              disabled={hasChildrenUnits}
              readOnly={!availableAddressInputs || hasChildrenUnits}
              onChange={(e) => handleChange("complement", e.target.value)}
            />
          </div>
        </div>

        <div className="mt-4 flex max-w-full flex-col items-center justify-center gap-4 md:flex-row md:px-1">
          <div className={fieldClass}>
            <label>Bairro *</label>
            <input
              type="text"
              value={address.neighborhood}
              readOnly={!availableAddressInputs ? true : false}
              onChange={(e) => handleChange("neighborhood", e.target.value)}
            />
          </div>

          <div className={fieldClass}>
            <label>Cidade *</label>
            <input
              type="text"
              value={address.city}
              readOnly={!availableAddressInputs ? true : false}
              onChange={(e) => handleChange("city", e.target.value)}
            />
          </div>

          <div className={fieldClass}>
            <label>UF *</label>
            <input
              type="text"
              value={address.uf}
              readOnly={!availableAddressInputs ? true : false}
              onChange={(e) => handleChange("uf", e.target.value)}
            />
          </div>

          <div className={fieldClass}>
            <label>
              Andar
              {hasChildrenUnits ? (
                <span className="ml-1 text-[10px] font-normal text-slate-500">
                  — definido nas unidades filhas
                </span>
              ) : null}
            </label>
            <input
              type="text"
              value={address.floor}
              disabled={hasChildrenUnits}
              readOnly={hasChildrenUnits}
              onChange={(e) => handleChange("floor", e.target.value)}
            />
          </div>
        </div>
      </div>

      {useManageChildren && propertyID ? (
        <PropertyChildrenSection
          mode="manage"
          propertyId={propertyID}
          allowStatusActions={false}
          onChildrenCountChange={handleChildrenCountChange}
        />
      ) : (
        <PropertyChildrenSection
          mode="local"
          value={localChildren}
          onChange={handleLocalChildrenChange}
          onChildrenCountChange={handleChildrenCountChange}
        />
      )}

      {!!"AIzaSyDSD2QM4JfZsREcRj_8StrW3E587RayIMc" && (
        <div
          style={{
            height: "300px",
            width: "100%",
            marginTop: "58px",
          }}
        >
          <GoogleMaps
            bootstrapURLKeys={{
              key: "AIzaSyDSD2QM4JfZsREcRj_8StrW3E587RayIMc",
            }}
            center={
              !!address.latitude && !!address.longitude
                ? {
                    lat: parseFloat(address.latitude),
                    lng: parseFloat(address.longitude),
                  }
                : {
                    lat: parseFloat("-3.724220"),
                    lng: parseFloat("-38.504030"),
                  }
            }
            defaultZoom={18}
            options={{
              mapTypeControl: true,
            }}
            layerTypes={["TrafficLayer", "TransitLayer"]}
            yesIWantToUseGoogleMapApiInternals={true}
          >
            <Marker
              lat={parseFloat(
                !!address.latitude ? address.latitude : "-3.724220"
              )}
              lng={parseFloat(
                !!address.longitude ? address.longitude : "-38.504030"
              )}
              color="#FF3838"
            />
          </GoogleMaps>
        </div>
      )}

      <ButtonsWrapper>
        <ResetButton
          type="button"
          onClick={() => {
            setAddress((prevValue) => ({
              ...prevValue,
              cep: "",
              city: "",
              complement: "",
              floor: "",
              address: "",
              neighborhood: "",
              number: "",
              uf: "",
            }));
          }}
        >
          LIMPAR
        </ResetButton>

        <NextButton
          type="button"
          onClick={isSubmiting ? () => {} : handleAddressSubmit}
        >
          <p>{isSubmiting ? "Carregando..." : "PRÓXIMO"}</p>
          {!isSubmiting && <ChevronRight className="h-[18px] w-[18px] text-white" />}
        </NextButton>
      </ButtonsWrapper>
    </form>
  );
}

export default MapForm;
